import React, { useState } from 'react';
import { customerView } from '../../utils/customer';
import { fmtDate } from '../../utils/dates';
import Modal from '../common/Modal';
import { EyeIcon } from '../common/icons';

const SOURCE_PILL = { demo_engagement: 'Engagement', checkin: 'Check-in', pos: 'Returning' };

function Fact({ label, value }) {
  if (value == null || value === '' || (Array.isArray(value) && !value.length)) return null;
  return (
    <div className="dp-fact">
      <dt>{label}</dt>
      <dd>{Array.isArray(value) ? value.join(', ') : value}</dd>
    </div>
  );
}

function History({ title, rows, render }) {
  return (
    <section className="dp-history">
      <h4>{title}</h4>
      {rows.length ? (
        <ul>
          {rows.slice(0, 15).map((row, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>{render(row)}</li>
          ))}
        </ul>
      ) : (
        <p className="dp-muted">Nothing on file yet.</p>
      )}
    </section>
  );
}

/** Customer on the bill: Nyafaz profile (membership, visits) or the name/phone typed for a walk-in. */
export default function CustomerCard({ bill, compact }) {
  const [open, setOpen] = useState(false);
  const c = customerView(bill);
  if (!c) return null;

  return (
    <section className={`dp-customer${compact ? ' dp-customer--compact' : ''}`}>
      <div className="dp-customer-head">
        <span className="dp-avatar dp-avatar--lg">{(c.name || 'G').trim().charAt(0).toUpperCase()}</span>
        <div className="dp-customer-name">
          <strong>{c.name}</strong>
          <span>{c.phone || 'No phone'}</span>
        </div>
        <span className={`dp-pill ${c.tier !== 'none' ? 'dp-pill--gold' : 'dp-pill--muted'}`}>
          {c.tier !== 'none' ? c.tier : c.fromNyafaz ? 'No membership' : SOURCE_PILL[c.source] || 'Walk-in'}
        </span>
      </div>
      <dl className="dp-facts">
        <Fact label="Visits" value={c.visits} />
        <Fact label="Last visit" value={c.lastVisit} />
        <Fact label="Member ID" value={c.memberId} />
        <Fact label="Points" value={c.points} />
        {!compact ? <Fact label="Allergies" value={c.allergies} /> : null}
      </dl>
      {c.fromNyafaz ? (
        <button type="button" className="dp-link dp-customer-more" onClick={() => setOpen(true)}>
          <EyeIcon size={15} /> Full profile & history
        </button>
      ) : null}

      {open ? (
        <Modal title={c.name} eyebrow="Customer profile · Nyafaz" onClose={() => setOpen(false)} size="wide">
          <dl className="dp-facts dp-facts--grid">
            <Fact label="Mobile" value={c.phone} />
            <Fact label="Membership" value={c.tier} />
            <Fact label="Member ID" value={c.memberId} />
            <Fact label="Points" value={c.points} />
            <Fact label="Plan ID" value={c.planId} />
            <Fact label="Plan credit" value={c.planCredit} />
            <Fact label="Age / gender" value={[c.age, c.gender].filter((x) => x != null && x !== '').join(' · ')} />
            <Fact label="Date of birth" value={c.dob ? fmtDate(c.dob) : null} />
            <Fact label="Anniversary" value={c.anniversary ? fmtDate(c.anniversary) : null} />
            <Fact label="Visits" value={c.visits} />
            <Fact label="Last visit" value={c.lastVisit} />
            <Fact label="Allergies" value={c.allergies} />
          </dl>
          <div className="dp-history-grid">
            <History
              title="Previous services"
              rows={c.previousServices}
              render={(s) => (
                <>
                  <span>{s.service_name}</span>
                  <small>{[s.franchise_name, fmtDate(s.selected_at || s.date)].filter(Boolean).join(' · ')}</small>
                </>
              )}
            />
            <History
              title="Treatments"
              rows={c.treatments}
              render={(t) => (
                <>
                  <span>{t.name}{t.quantity ? ` ×${t.quantity}` : ''}</span>
                  <small>{fmtDate(t.date)}</small>
                </>
              )}
            />
            <History
              title="Products"
              rows={c.products}
              render={(p) => (
                <>
                  <span>{p.name}{p.quantity ? ` ×${p.quantity}` : ''}</span>
                  <small>{fmtDate(p.date)}</small>
                </>
              )}
            />
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
